import { useState, useEffect, useMemo, useCallback } from 'react'
import { format } from 'date-fns'
import { BusinessOpeningHour } from '@/types'

// Types for the unified display hours system
export interface SimpleDisplayHoursRange {
  start: number // Hour (0-23, can extend beyond for overnight)
  end: number   // Hour (1-36, max 12:00 next day for overnight)
  startTime: string // HH:MM format
  endTime: string   // HH:MM format
}

export interface SimpleDisplayHoursResult {
  range: SimpleDisplayHoursRange
  calculationMethod: 'user-override' | 'opening-hours' | 'fallback'
  canExpand: boolean
  expandRange: (newRange: Partial<SimpleDisplayHoursRange>) => void
  resetToAuto: () => void
  isLoading: boolean
}

export interface SimpleDisplayHoursOptions {
  businessId: string
  date: string
  openingHours?: BusinessOpeningHour[]
  shifts?: Array<{ startTime: string; endTime: string }> // Optional shifts for unified calculation
  mode?: 'single-day' | 'multi-day' // 'multi-day' uses most expansive range across all days (for month/week views)
}

/**
 * Unified Display Hours Hook
 *
 * Calculates display hours using the most expansive range from shifts AND opening hours:
 * - Start: min(earliestShiftStart, openingHourStart) - 1hr padding
 * - End: max(latestShiftEnd, openingHourClose) + 1hr padding
 * - Fallback: 8:00-18:00 (if no shifts AND no opening hours)
 *
 * User can override via expandRange(), persisted to localStorage.
 */
export function useSimpleDisplayHours(options: SimpleDisplayHoursOptions): SimpleDisplayHoursResult {
  const { businessId, date, openingHours = [], shifts = [], mode = 'single-day' } = options

  // State for user overrides
  const [userOverride, setUserOverride] = useState<SimpleDisplayHoursRange | null>(null)
  const [expansionHistory, setExpansionHistory] = useState<Record<string, SimpleDisplayHoursRange>>({})
  const [isLoading, setIsLoading] = useState(true)

  // Load user preferences from localStorage
  useEffect(() => {
    const loadPreferences = () => {
      try {
        const stored = localStorage.getItem(`simple-display-hours-${businessId}`)
        if (stored) {
          const prefs = JSON.parse(stored)
          setExpansionHistory(prefs.expansionHistory || {})
          setUserOverride(prefs.userOverride || null)
        }
      } catch (error) {
        console.warn('Failed to load simple display hour preferences:', error)
      }
      setIsLoading(false)
    }

    loadPreferences()
  }, [businessId])

  // Calculate display hours with unified priority system
  // Uses most expansive range: min(shifts, opening) for start, max(shifts, opening) for end
  const { calculatedRange, calculationMethod } = useMemo(() => {
    // 1. Check for date-specific expansion history (user override)
    if (expansionHistory[date]) {
      return {
        calculatedRange: expansionHistory[date],
        calculationMethod: 'user-override' as const
      }
    }

    // 2. Check for global user override
    if (userOverride) {
      return {
        calculatedRange: userOverride,
        calculationMethod: 'user-override' as const
      }
    }

    // 3. Unified calculation: combine shifts AND opening hours for most expansive range
    let openStart: number | null = null
    let openEnd: number | null = null
    let shiftStart: number | null = null
    let shiftEnd: number | null = null

    // Get opening hours based on mode
    if (openingHours.length > 0) {
      if (mode === 'multi-day') {
        // Multi-day mode: find most expansive range across ALL days
        for (const oh of openingHours) {
          if (!oh.is_closed_all_day && oh.opening_time && oh.closing_time) {
            try {
              const thisOpen = parseInt(oh.opening_time.split(':')[0])
              const thisClose = parseInt(oh.closing_time.split(':')[0])
              if (openStart === null || thisOpen < openStart) openStart = thisOpen
              if (openEnd === null || thisClose > openEnd) openEnd = thisClose
            } catch (error) {
              console.warn('Error parsing opening hours:', error)
            }
          }
        }
      } else {
        // Single-day mode: get hours for specific day (parse date parts to avoid UTC issues)
        const [year, month, day] = date.split('-').map(Number)
        const localDate = new Date(year, month - 1, day)
        const dayOfWeek = format(localDate, 'EEEE') // "Monday", "Tuesday", etc.
        const dayHours = openingHours.find(oh => oh.day_of_week === dayOfWeek)

        if (dayHours && !dayHours.is_closed_all_day && dayHours.opening_time && dayHours.closing_time) {
          try {
            openStart = parseInt(dayHours.opening_time.split(':')[0])
            openEnd = parseInt(dayHours.closing_time.split(':')[0])
          } catch (error) {
            console.warn('Error parsing opening hours:', error)
          }
        }
      }
    }

    // Get shift range (earliest start, latest end)
    if (shifts.length > 0) {
      const shiftStarts = shifts.map(s => parseInt(s.startTime.split(':')[0]))
      const shiftEnds = shifts.map(s => {
        const hour = parseInt(s.endTime.split(':')[0])
        // Handle overnight shifts (00:XX-06:XX next day can be represented as 24-30)
        return hour
      })
      shiftStart = Math.min(...shiftStarts)
      shiftEnd = Math.max(...shiftEnds)
    }

    // Calculate unified range (most expansive)
    let displayStart: number
    let displayEnd: number
    let method: 'opening-hours' | 'shifts' | 'combined' | 'fallback'

    if (shiftStart !== null && openStart !== null) {
      // Both available: take most expansive range
      displayStart = Math.min(shiftStart, openStart)
      displayEnd = Math.max(shiftEnd!, openEnd!)
      method = 'combined' as const
    } else if (shiftStart !== null) {
      // Only shifts available
      displayStart = shiftStart
      displayEnd = shiftEnd!
      method = 'shifts' as const
    } else if (openStart !== null) {
      // Only opening hours available
      displayStart = openStart
      displayEnd = openEnd!
      method = 'opening-hours' as const
    } else {
      // Fallback to business hours (8:00-18:00)
      return {
        calculatedRange: {
          start: 8,
          end: 18,
          startTime: '08:00',
          endTime: '18:00'
        },
        calculationMethod: 'fallback' as const
      }
    }

    // Add 1-hour padding
    const paddedStart = Math.max(0, displayStart - 1)
    const paddedEnd = Math.min(36, displayEnd + 1) // Cap at 36 (12:00 next day)

    return {
      calculatedRange: {
        start: paddedStart,
        end: paddedEnd,
        startTime: formatHour(paddedStart),
        endTime: formatHour(paddedEnd)
      },
      // Report as opening-hours for backward compatibility (UI treats combined/shifts/opening-hours similarly)
      calculationMethod: (method === 'fallback' ? 'fallback' : 'opening-hours') as const
    }
  }, [expansionHistory, userOverride, openingHours, shifts, date, mode])

  // Expand range function - allows user to override calculated display hours
  const expandRange = useCallback((newRange: Partial<SimpleDisplayHoursRange>) => {
    const expandedRange: SimpleDisplayHoursRange = {
      start: newRange.start ?? calculatedRange.start,
      end: newRange.end ?? calculatedRange.end,
      startTime: newRange.startTime ?? formatHour(newRange.start ?? calculatedRange.start),
      endTime: newRange.endTime ?? formatHour(newRange.end ?? calculatedRange.end)
    }

    // Use functional update to get current state (avoids stale closure)
    setExpansionHistory(currentHistory => {
      const newExpansionHistory = {
        ...currentHistory,
        [date]: expandedRange
      }

      // Save to localStorage
      try {
        const prefs = {
          expansionHistory: newExpansionHistory,
          userOverride: userOverride
        }
        localStorage.setItem(`simple-display-hours-${businessId}`, JSON.stringify(prefs))
      } catch (error) {
        console.warn('Failed to save simple display hour preferences:', error)
      }

      return newExpansionHistory
    })
  }, [calculatedRange, date, businessId, userOverride])

  // Reset to automatic calculation
  const resetToAuto = useCallback(() => {
    setExpansionHistory(currentHistory => {
      const newExpansionHistory = { ...currentHistory }
      delete newExpansionHistory[date]

      // Save to localStorage
      try {
        const prefs = {
          expansionHistory: newExpansionHistory,
          userOverride: null
        }
        localStorage.setItem(`simple-display-hours-${businessId}`, JSON.stringify(prefs))
      } catch (error) {
        console.warn('Failed to save simple display hour preferences:', error)
      }

      return newExpansionHistory
    })
    setUserOverride(null)
  }, [date, businessId])

  return {
    range: calculatedRange,
    calculationMethod,
    canExpand: true,
    expandRange,
    resetToAuto,
    isLoading
  }
}

// Helper function to format hour number to HH:MM string
function formatHour(hour: number): string {
  const displayHour = Math.max(0, hour)
  return `${displayHour.toString().padStart(2, '0')}:00`
}